import { Fragment } from 'react';

import { PendencyTeamCard } from '../../components/cards/PendencyTeamCard';

import { groupByCategory, groupByOperator, groupByTimeline } from './tela4-adapter';

import type { HeatmapRowVM, PendencyCardVM, PendencyGroupVM } from '../../view-models';
import type React from 'react';

const DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function GroupedView({
  groups,
  selected,
  onToggleSelect,
  onToggleGroup,
  onReassign,
  onPostpone,
  onResolve,
  onOpenConversation,
}: {
  groups: PendencyGroupVM[];
  selected?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleGroup?: (ids: string[], allSelected: boolean) => void;
  onReassign?: (id: string) => void;
  onPostpone?: (id: string) => void;
  onResolve?: (card: PendencyCardVM) => void;
  onOpenConversation?: (conversationId: string) => void;
}): React.JSX.Element {
  return (
    <div className="ti-view">
      {groups.map((g) => {
        const groupIds = g.items.map((p) => p.id);
        const allSelected =
          groupIds.length > 0 && groupIds.every((id) => selected?.has(id) === true);
        return (
          <details
            key={g.key}
            className={'ti-group' + (g.danger ? ' ti-group--danger' : '')}
            open={g.danger || g.items.length >= 3}
          >
            <summary className="ti-group__head">
              {onToggleGroup !== undefined && (
                <input
                  type="checkbox"
                  className="pend-team-card__check"
                  checked={allSelected}
                  aria-label={`Selecionar grupo ${g.key}`}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onChange={() => {
                    onToggleGroup(groupIds, allSelected);
                  }}
                />
              )}
              {g.icon !== undefined && <span style={{ fontSize: 14 }}>{g.icon}</span>}
              <b style={{ color: g.danger ? 'var(--danger-fg)' : 'var(--fg-1)' }}>{g.key}</b>
              <span className="tweaks-hint">
                ({g.items.length} {g.items.length === 1 ? 'aberta' : 'abertas'}
                {g.overdueCount > 0
                  ? `, ${g.overdueCount} ${g.overdueCount === 1 ? 'vencida' : 'vencidas'}`
                  : ''}
                )
              </span>
            </summary>
            <div className="ti-group__body">
              {g.items.map((p) => (
                <PendencyTeamCard
                  key={p.id}
                  p={p}
                  {...(selected !== undefined && { selected: selected.has(p.id) })}
                  {...(onToggleSelect !== undefined && { onToggleSelect })}
                  {...(onReassign !== undefined && { onReassign })}
                  {...(onPostpone !== undefined && { onPostpone })}
                  {...(onResolve !== undefined && { onResolve })}
                  {...(onOpenConversation !== undefined && { onOpenConversation })}
                />
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
}

function HeatmapView({ rows }: { rows: HeatmapRowVM[] }): React.JSX.Element {
  return (
    <div className="ti-heatmap">
      <div className="ti-heatmap__grid">
        <div className="ti-heatmap__corner" />
        {DAYS.map((d) => (
          <div key={d} className="ti-heatmap__head">
            {d}
          </div>
        ))}
        {rows.map((row) => (
          <Fragment key={row.op}>
            <div className="ti-heatmap__row-label">{row.op}</div>
            {row.days.map((v, i) => {
              const abs = Math.abs(v);
              const overdue = v < 0;
              const tone = abs === 0 ? 'empty' : abs <= 2 ? 'green' : abs <= 5 ? 'yellow' : 'red';
              return (
                <div
                  key={i}
                  className={
                    `ti-heatmap__cell ti-heatmap__cell--${tone}` +
                    (overdue ? ' ti-heatmap__cell--overdue' : '')
                  }
                  title={`${row.op} · ${DAYS[i] ?? ''} · ${abs} pendências${overdue ? ' (com vencidas)' : ''}`}
                >
                  {abs === 0 ? '–' : abs}
                  {overdue ? ' ⚠' : ''}
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
      <div className="ti-heatmap__legend tweaks-hint">
        <span className="ti-heatmap__legend-dot" style={{ background: 'var(--success-fg)' }} />
        ≤2
        <span
          className="ti-heatmap__legend-dot"
          style={{ background: 'var(--warning-fg)', marginLeft: 8 }}
        />
        3-5
        <span
          className="ti-heatmap__legend-dot"
          style={{ background: 'var(--danger-fg)', marginLeft: 8 }}
        />
        &gt;5
        <span style={{ marginLeft: 16 }}>⚠ indica pendência vencida</span>
      </div>
    </div>
  );
}

export function TeamInboxView({
  cards,
  view,
  heatmap,
  selected,
  onToggleSelect,
  onToggleGroup,
  onReassign,
  onPostpone,
  onResolve,
  onOpenConversation,
}: {
  cards: PendencyCardVM[];
  view: 'operator' | 'category' | 'timeline' | 'heatmap';
  heatmap: HeatmapRowVM[];
  selected?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleGroup?: (ids: string[], allSelected: boolean) => void;
  onReassign?: (id: string) => void;
  onPostpone?: (id: string) => void;
  onResolve?: (card: PendencyCardVM) => void;
  onOpenConversation?: (conversationId: string) => void;
}): React.JSX.Element | null {
  const selectionProps = {
    ...(selected !== undefined && { selected }),
    ...(onToggleSelect !== undefined && { onToggleSelect }),
    ...(onToggleGroup !== undefined && { onToggleGroup }),
    ...(onReassign !== undefined && { onReassign }),
    ...(onPostpone !== undefined && { onPostpone }),
    ...(onResolve !== undefined && { onResolve }),
    ...(onOpenConversation !== undefined && { onOpenConversation }),
  };
  if (view === 'operator')
    return <GroupedView groups={groupByOperator(cards)} {...selectionProps} />;
  if (view === 'category')
    return <GroupedView groups={groupByCategory(cards)} {...selectionProps} />;
  if (view === 'timeline')
    return <GroupedView groups={groupByTimeline(cards)} {...selectionProps} />;
  return <HeatmapView rows={heatmap} />;
}
